import { useState } from "react";
import { FieldErrors, useFormContext, UseFormRegister } from "react-hook-form";
import styles from "./index.module.scss";
import classNames from "classnames";
import ErrorMessage from "../ErrorMessage";
import PreviewIconOff from "src/assets/images/preview_off_icon.svg";
import PreviewIcon from "src/assets/images/preview_icon.svg";
import XButtonIcon from "src/assets/images/x_button.svg";
import { useTranslation } from "react-i18next";

interface IFormInputProps {
  label?: string | React.ReactNode;
  name: string;
  register: UseFormRegister<any>;
  errors?: FieldErrors<any>;
  placeholder?: string;
  type?: "text" | "email" | "password" | "number" | "tel" | "textarea";
  autoFocus?: boolean;
  min?: number;
  max?: number;
  required?: boolean;
  children?: React.ReactNode;
  autoComplete?: "on" | "off";
  width?: number;
  height?: number;
  value?: string | number;
  // 성공 메시지 표시
  success?: {
    isSuccess: boolean;
    message: string;
  };
  disabled?: boolean;
  isClearBtn?: boolean;
  hint?: string; // 힌트 문구
}

const FormInput = ({
  type,
  errors,
  name,
  register,
  label,
  placeholder,
  autoFocus,
  min,
  max,
  children,
  autoComplete = "off",
  width,
  value,
  required = false,
  success,
  disabled,
  height,
  isClearBtn = true,
  hint,
}: IFormInputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const error = errors?.[name];
  const [inputValue, setInputValue] = useState("");
  const isSuccess = success && success.isSuccess;
  // const { onChange, onBlur, name: inputName, ref } = register(name);
  const { watch, setValue } = useFormContext();
  const watchedValue = watch(name); // RHF 상태 감시
  // 버튼들의 존재 여부에 따라 input padding 계산
  const hasPasswordToggle = type === "password";
  const hasClearButton = !!watchedValue;
  const { t } = useTranslation();
  // padding-right 계산: clearButton(40px) + passwordToggle(40px) + 기본 padding(16px)
  const calculatePaddingRight = () => {
    if (hasPasswordToggle && hasClearButton) {
      return 88; // 40 + 40 + 8 (간격)
    } else if (hasPasswordToggle || hasClearButton) {
      return 48; // 40 + 8
    }
    return 16; // 기본 padding
  };

  return (
    <div className={styles.inputField}>
      {label && (
        <label htmlFor={name} className={styles.inputLabel}>
          {label} {required && <span className={styles.required}>*</span>}
        </label>
      )}
      <div className={styles.inputWrapper}>
        {type === "textarea" ? (
          <textarea
            id={name}
            style={{ width, height }}
            placeholder={placeholder}
            className={classNames(styles.formInput, styles.textarea)}
            autoFocus={autoFocus}
            {...register(name)}
          />
        ) : (
          <input
            type={
              type === "password" ? (showPassword ? "text" : "password") : type
            }
            id={name}
            style={{
              width,
              height,
              paddingRight: `${calculatePaddingRight()}px`,
            }}
            placeholder={placeholder}
            autoFocus={autoFocus}
            min={min}
            max={max}
            autoComplete={autoComplete}
            disabled={disabled}
            className={classNames(styles.formInput, !!error && styles.invalid)}
            {...register(name)}
          />
        )}

        {/* Clear Button - passwordToggle이 있으면 더 왼쪽에, 없으면 오른쪽에 배치 */}
        {hasClearButton && isClearBtn && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={() => {
              setValue(name, ""); // react-hook-form의 값 직접 초기화
            }}
            aria-label="입력값 지우기"
            tabIndex={-1}
            style={{
              right: hasPasswordToggle ? "40px" : "12px",
            }}
          >
            <img src={XButtonIcon} alt="입력값 지우기" />
            <span className={styles.tooltip}>{t("common.tooltip.clear")}</span>
          </button>
        )}

        {/* Password Toggle Button - 항상 제일 오른쪽에 위치 */}
        {type === "password" && (
          <button
            type="button"
            className={styles.passwordToggle}
            onClick={() => setShowPassword((prev) => !prev)}
            tabIndex={-1}
            aria-label={
              showPassword
                ? t("common.tooltip.hidePassword")
                : t("common.tooltip.showPassword")
            }
            style={{ right: "12px" }}
          >
            <img
              src={showPassword ? PreviewIconOff : PreviewIcon}
              alt={
                showPassword
                  ? t("common.tooltip.hidePassword")
                  : t("common.tooltip.showPassword")
              }
            />
            <span className={styles.tooltip}>
              {showPassword
                ? t("common.tooltip.hidePassword")
                : t("common.tooltip.showPassword")}
            </span>
          </button>
        )}

        <span className={styles.childrenContainer}>{children}</span>
      </div>
      {error && (
        <ErrorMessage
          type="error"
          message={error?.message as string}
        ></ErrorMessage>
      )}
      {isSuccess && (
        <ErrorMessage type="success" message={success.message}></ErrorMessage>
      )}
      {hint && !error && !isSuccess && (
        <span className={styles.hint}>{hint}</span>
      )}
    </div>
  );
};

export default FormInput;
